import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import {
  Observable,
  catchError,
  finalize,
  firstValueFrom,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthMeResponseDto,
  AuthProvider,
  AuthUser,
  ChangePasswordRequestDto,
  ClaimDto,
  ConfirmPasswordResetDto,
  InitiatePasswordResetRequestDto,
  LoginRequestDto,
  LoginResponseDto,
  PasswordResetInitiatedDto,
} from './auth.model';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';
const AUTH_PROVIDER_KEY = 'auth_provider';

const JWT_CLAIM_EMAIL = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';
const JWT_CLAIM_ROLE = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const JWT_CLAIM_DEALER_ID = 'http://schemas.samob.com/identity/claims/dealerId';
const JWT_CLAIM_CONTACT_ID = 'http://schemas.samob.com/identity/claims/contactId';
const KNOWN_ROLE_NAMES: Record<string, string> = {
  admin: 'Admin',
  dealer: 'Dealer',
  dealeremployee: 'Employee',
  employee: 'Employee',
  customer: 'Customer',
};

const oidcConfig: AuthConfig = {
  issuer: environment.oidcAuthority,
  clientId: environment.oidcClientId,
  redirectUri: environment.oidcRedirectUri,
  postLogoutRedirectUri: environment.oidcPostLogoutRedirectUri,
  responseType: 'code',
  scope: environment.oidcScope,
  // Azure AD endpoints don't share the same base as the issuer URL,
  // so strict validation must be disabled for Azure AD to work.
  strictDiscoveryDocumentValidation: false,
  // PKCE is enabled by default with responseType: 'code'
  showDebugInformation: !environment.production,
  clearHashAfterLogin: false,
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly oauthService = inject(OAuthService);
  private validateSessionRequest$: Observable<boolean> | null = null;

  readonly user = signal<AuthUser | null>(null);
  readonly token = signal<string | null>(null);
  readonly authProvider = signal<AuthProvider | null>(null);
  readonly isPasswordChangeRequired = signal<boolean>(false);
  readonly isAuthenticated = computed(() => !!this.token() && !!this.user());
  readonly roles = computed(() => this.user()?.roles ?? []);
  readonly role = computed(() => this.roles()[0] ?? null);
  readonly dealerId = computed(() => this.user()?.dealerId ?? null);
  readonly contactId = computed(() => this.user()?.contactId ?? null);

  constructor() {
    this.oauthService.configure(oidcConfig);
  }

  /** Restore auth state from localStorage on app load */
  initializeAuth(): void {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedUser = localStorage.getItem(AUTH_USER_KEY);
    const storedProvider = localStorage.getItem(AUTH_PROVIDER_KEY);

    if (!storedToken) {
      this.clearAuthState();
      return;
    }

    this.token.set(storedToken);

    if (storedProvider === 'oidc' || storedProvider === 'password') {
      this.authProvider.set(storedProvider);
    }

    if (!storedUser) {
      return;
    }

    try {
      const user: AuthUser = JSON.parse(storedUser);
      user.roles = this.normalizeRoles(user.roles);

      if (!user.dealerId) {
        user.dealerId = this.extractDealerIdFromToken(storedToken);
      }
      if (!user.contactId) {
        user.contactId = this.extractContactIdFromToken(storedToken);
      }

      this.user.set(user);
      this.isPasswordChangeRequired.set(user.isPasswordChangeRequired ?? false);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));

      if (
        storedProvider === 'password' &&
        this.hasAnyRole(user.roles, ['Dealer', 'Employee']) &&
        !user.dealerId
      ) {
        this.validateSession().subscribe();
      }
    } catch {
      localStorage.removeItem(AUTH_USER_KEY);
    }
  }

  setAuth(token: string, user: AuthUser, provider: AuthProvider): void {
    this.token.set(token);
    this.user.set(user);
    this.authProvider.set(provider);
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    localStorage.setItem(AUTH_PROVIDER_KEY, provider);
  }

  loginWithPassword(email: string, password: string): Observable<LoginResponseDto> {
    const body: LoginRequestDto = { email, password };
    return this.http.post<LoginResponseDto>(`${environment.apiUrl}/Auth/login`, body).pipe(
      switchMap((response) => {
        const accessToken = response.accessToken;

        if (!accessToken) {
          return of(response);
        }

        return this.http
          .get<AuthMeResponseDto>(`${environment.apiUrl}/Auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
          .pipe(
            map((authMeResponse) => {
              const user = this.mapAuthMeResponseToUser(authMeResponse) ?? {
                email: response.email ?? email,
                roles: response.roles ?? [],
                dealerId: null,
              };

              const dealerId = user.dealerId ?? this.extractDealerIdFromToken(accessToken);
              const contactId = user.contactId ?? this.extractContactIdFromToken(accessToken);
              const isPasswordChangeRequired = response.isPasswordChangeRequired ?? false;

              this.isPasswordChangeRequired.set(isPasswordChangeRequired);
              this.setAuth(
                accessToken,
                {
                  email: user.email,
                  roles:
                    user.roles.length > 0
                      ? user.roles
                      : this.normalizeRoles(response.roles ?? []),
                  dealerId,
                  contactId,
                  isPasswordChangeRequired,
                },
                'password',
              );

              return response;
            }),
            catchError(() => {
              const fallbackEmail = response.email ?? email;
              const dealerId = this.extractDealerIdFromToken(accessToken);
              const contactId = this.extractContactIdFromToken(accessToken);
              const isPasswordChangeRequired = response.isPasswordChangeRequired ?? false;

              this.isPasswordChangeRequired.set(isPasswordChangeRequired);
              this.setAuth(
                accessToken,
                {
                  email: fallbackEmail,
                  roles: this.normalizeRoles(response.roles ?? []),
                  dealerId,
                  contactId,
                  isPasswordChangeRequired,
                },
                'password',
              );

              return of(response);
            }),
          );
      }),
    );
  }

  changePassword(dto: ChangePasswordRequestDto): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/Auth/change-password`, dto);
  }

  initiatePasswordReset(email: string): Observable<PasswordResetInitiatedDto> {
    const payload: InitiatePasswordResetRequestDto = { email };
    return this.http.post<PasswordResetInitiatedDto>(
      `${environment.apiUrl}/Auth/initiate-password-reset`,
      payload,
    );
  }

  confirmPasswordReset(dto: ConfirmPasswordResetDto): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/Auth/confirm-password-reset`, dto);
  }

  /** Initiates OIDC Authorization Code + PKCE flow — redirects to Azure AD */
  initiateOidcLogin(): void {
    // Discovery doc must be loaded before initCodeFlow() knows the authorization endpoint
    this.oauthService.loadDiscoveryDocument().then(() => {
      this.oauthService.initCodeFlow();
    });
  }

  /**
   * Called at /auth/callback. Loads the discovery doc, then processes the
   * authorization code and exchanges it for a token via `angular-oauth2-oidc`.
   * The resulting access_token is sent to our backend's /Auth/me to get the user.
   */
  async handleOidcCallback(): Promise<void> {
    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      const accessToken = this.oauthService.getAccessToken();

      if (!accessToken) {
        this.router.navigate(['/login']);
        return;
      }

      // Fetch current user from our backend using the Microsoft token
      const response = await firstValueFrom(
        this.http.get<AuthMeResponseDto>(`${environment.apiUrl}/Auth/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      );

      const user = this.mapAuthMeResponseToUser(response);

      if (user) {
        this.setAuth(accessToken, user, 'oidc');
        this.redirectToHome();
      } else {
        this.router.navigate(['/login']);
      }
    } catch {
      this.router.navigate(['/login']);
    }
  }

  getCurrentUser(): Observable<AuthMeResponseDto> {
    return this.http.get<AuthMeResponseDto>(`${environment.apiUrl}/Auth/me`);
  }

  validateSession(): Observable<boolean> {
    if (!this.token()) {
      return of(false);
    }

    if (this.validateSessionRequest$) {
      return this.validateSessionRequest$;
    }

    this.validateSessionRequest$ = this.getCurrentUser().pipe(
      map((response) => {
        const user = this.mapAuthMeResponseToUser(response);
        if (!user || user.roles.length === 0) {
          this.clearAuthState();
          return false;
        }

        if (!user.dealerId) {
          user.dealerId = this.extractDealerIdFromToken(this.token());
        }
        if (!user.contactId) {
          user.contactId = this.extractContactIdFromToken(this.token());
        }

        this.user.set(user);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
        return true;
      }),
      catchError(() => {
        this.clearAuthState();
        return of(false);
      }),
      finalize(() => {
        this.validateSessionRequest$ = null;
      }),
      shareReplay(1),
    );

    return this.validateSessionRequest$;
  }

  logout(): void {
    const provider = this.authProvider();
    const token = this.token();

    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;

    this.http.post(`${environment.apiUrl}/Auth/logout`, {}, { headers }).subscribe();

    this.clearClientSession();

    if (provider === 'oidc') {
      this.oauthService.logOut();
      return;
    }

    this.navigateToLogin();
  }

  handleUnauthorizedResponse(): void {
    this.clearClientSession();
    this.navigateToLogin();
  }

  clearSession(): void {
    this.clearClientSession();
  }

  redirectToLogin(): void {
    this.navigateToLogin();
  }

  redirectToHome(): void {
    this.router.navigate([this.getHomePath()]);
  }

  getHomePath(): string {
    const roles = this.roles();
    if (roles.includes('Admin')) {
      return '/dealers';
    }

    if (this.hasAnyRole(roles, ['Dealer', 'Employee'])) {
      return '/customers';
    }

    if (roles.includes('Customer')) {
      return '/agreements';
    }

    return '/login';
  }

  redirectToDashboard(): void {
    this.redirectToHome();
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasRoleValue(role: string): boolean {
    return this.hasRole(role);
  }

  private mapAuthMeResponseToUser(response: AuthMeResponseDto): AuthUser | null {
    const claims = response.claims ?? [];

    const email = this.firstNonEmpty([
      response.email,
      this.getClaimValue(claims, 'email'),
      this.getClaimValue(claims, 'preferred_username'),
      this.getClaimValue(claims, 'upn'),
      this.getClaimValue(claims, JWT_CLAIM_EMAIL),
      this.normalizeMailHash(response.name),
      response.name,
    ]);

    const roles = this.extractRoles(response);

    if (!email) {
      return null;
    }

    return {
      email,
      roles,
      dealerId: this.extractDealerId(response),
      contactId: this.extractContactId(response),
    };
  }

  private extractDealerId(response: AuthMeResponseDto): string | null {
    if (typeof response.dealerId === 'string' && response.dealerId.trim().length > 0) {
      return response.dealerId;
    }

    const claims = response.claims ?? [];
    const dealerClaimTypes = ['dealerId', 'DealerId', 'dealer_id'];

    return this.firstNonEmpty(
      dealerClaimTypes.map((claimType) => this.getClaimValue(claims, claimType)),
    );
  }

  private extractContactId(response: AuthMeResponseDto): string | null {
    if (typeof response.contactId === 'string' && response.contactId.trim().length > 0) {
      return response.contactId;
    }

    const claims = response.claims ?? [];
    const contactClaimTypes = ['contactId', 'ContactId', 'contact_id'];

    return this.firstNonEmpty(
      contactClaimTypes.map((claimType) => this.getClaimValue(claims, claimType)),
    );
  }

  private extractDealerIdFromToken(token: string | null): string | null {
    if (!token) {
      return null;
    }

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }

      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded)) as Record<string, unknown>;

      const values = [
        payload['dealerId'],
        payload['DealerId'],
        payload['dealer_id'],
        payload[JWT_CLAIM_DEALER_ID],
      ];

      for (const value of values) {
        if (typeof value === 'string' && value.trim().length > 0) {
          return value;
        }
      }
    } catch {
      return null;
    }

    return null;
  }

  private extractContactIdFromToken(token: string | null): string | null {
    if (!token) {
      return null;
    }

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }

      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded)) as Record<string, unknown>;

      const values = [
        payload['contactId'],
        payload['ContactId'],
        payload['contact_id'],
        payload[JWT_CLAIM_CONTACT_ID],
      ];

      for (const value of values) {
        if (typeof value === 'string' && value.trim().length > 0) {
          return value;
        }
      }
    } catch {
      return null;
    }

    return null;
  }

  private extractRoles(response: AuthMeResponseDto): string[] {
    const roleList: string[] = [];

    if (Array.isArray(response.roles)) {
      roleList.push(...response.roles);
    }

    if (Array.isArray(response.role)) {
      roleList.push(...response.role);
    } else if (typeof response.role === 'string') {
      roleList.push(response.role);
    }

    const claims = response.claims ?? [];
    const roleClaimTypes = ['role', 'roles', JWT_CLAIM_ROLE];

    for (const claimType of roleClaimTypes) {
      const values = this.getClaimValues(claims, claimType);
      roleList.push(...values);
    }

    return this.normalizeRoles(roleList);
  }

  private normalizeRoles(roles: (string | null | undefined)[]): string[] {
    const normalized = roles
      .map((role) => {
        const trimmed = role?.trim();
        if (!trimmed) {
          return null;
        }

        return KNOWN_ROLE_NAMES[trimmed.toLowerCase()] ?? trimmed;
      })
      .filter((role): role is string => !!role);

    return Array.from(new Set(normalized));
  }

  private hasAnyRole(roles: string[], expectedRoles: string[]): boolean {
    return expectedRoles.some((role) => roles.includes(role));
  }

  private getClaimValue(claims: ClaimDto[], claimType: string): string | null {
    const claim = claims.find((item) => item.type === claimType && !!item.value);
    return claim?.value ?? null;
  }

  private getClaimValues(claims: ClaimDto[], claimType: string): string[] {
    return claims
      .filter((item) => item.type === claimType && !!item.value)
      .map((item) => item.value);
  }

  private normalizeMailHash(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    if (value.startsWith('mail#')) {
      return value.slice('mail#'.length);
    }

    return value;
  }

  private firstNonEmpty(values: (string | null | undefined)[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return null;
  }

  private clearStorage(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_PROVIDER_KEY);
  }

  private clearClientSession(): void {
    this.dialog.closeAll();
    this.clearAuthState();
  }

  private navigateToLogin(): void {
    if (this.router.url !== '/login') {
      void this.router.navigate(['/login']);
    }
  }

  clearPasswordChangeRequired(): void {
    this.isPasswordChangeRequired.set(false);
    const user = this.user();
    if (user) {
      const updatedUser: AuthUser = { ...user, isPasswordChangeRequired: false };
      this.user.set(updatedUser);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(updatedUser));
    }
  }

  private clearAuthState(): void {
    this.clearStorage();
    this.token.set(null);
    this.user.set(null);
    this.authProvider.set(null);
    this.isPasswordChangeRequired.set(false);
  }
}
